/**
 * FairwayBid – sich an ein Bündel anhängen
 *
 * "Anhängen" hieß bisher: ein leeres Anfrageformular. Das ist keine
 * Bündelbestellung, das ist eine neue Anfrage neben einer anderen – man
 * könnte dort eine ganz andere Kategorie wählen, und dann gehört nichts mehr
 * zusammen.
 *
 * Angehängt wird deshalb jetzt eine Kopie: Was gleich sein MUSS, damit ein
 * Anbieter ein Sammelangebot rechnen kann, wird übernommen. Was
 * unterschiedlich sein muss, bleibt leer.
 *
 * Was gleich sein muss, steht in bundles.js (BUNDLE_MATCH_FIELDS) – dieselbe
 * Regel, nach der auch der Pool gruppiert. Bei Range-Bällen sind das
 * Ausführung und Farbe, bei allen anderen Kategorien nur das Produkt selbst.
 *
 *   übernommen   Kategorie, die bindenden Merkmale, "Bündel: ja"
 *   nicht        Menge, Budget, Lieferland, Termin, Frist, Beschreibung
 *
 * Der Logodruck wird ausdrücklich NICHT übernommen: Jede Anlage druckt ihr
 * eigenes Logo. Bei einer Bündelbestellung kommen die Bälle aus einer Charge,
 * der Druck wird danach zwischen jeder Anlage und dem Anbieter einzeln
 * vereinbart – der Hinweis dazu wandert mit ins Formular.
 *
 * Die Menge ist die eine Ausnahme: Wer sie im Pool schon eingestellt hat, um
 * die Gesamtmenge zu sehen, soll sie nicht zweimal eintragen.
 *
 * Die Beschreibung wird bewusst nicht kopiert. Dort steht die Range-Länge
 * einer fremden Anlage, deren Altgerät und deren Anschluss - das ist der
 * Bedarf des anderen, nicht der eigene. Die Kategorievorlage aus
 * templates.js füllt stattdessen den Text.
 *
 * Übergabe über sessionStorage statt über die Adresszeile: Es sind mehrere
 * Felder, sie gehen niemanden außer diesem Browser etwas an, und in der URL
 * würden sie beim Teilen des Links mitwandern.
 */

import { matchFelderFuer, getCategory, bundleHinweis } from './bundles';
import { MENGE_KEY, STAFFEL_KEY, mengenfeldFuer } from './quantityField';

const SCHLUESSEL = 'fwb.buendel.vorlage';

// Welches Feld die Stückzahl trägt, entscheidet quantityField - dieselbe
// Stelle, aus der das Anfrageformular sein Feld bezieht. Zwei Antworten auf
// dieselbe Frage wären genau die Art Fehler, die niemand bemerkt, bis die
// Menge im Formular fehlt.
export const MENGENFELD = STAFFEL_KEY;
export { mengenfeldFuer };

/**
 * Baut die Vorlage aus einer fremden Anfrage.
 *
 * @param {Object} listing die Anfrage, an die angehängt wird
 * @param {Object} extras { menge, quelle }
 */
export const baueVorlage = (listing, extras = {}) => {
  const pd = listing?.attributes?.publicData || {};
  const werte = {};

  if (pd.categoryLevel1) werte.categoryLevel1 = pd.categoryLevel1;
  if (pd.categoryLevel2) werte.categoryLevel2 = pd.categoryLevel2;

  // Nur die Felder, die diese Kategorie bindend macht. Alles andere darf die
  // anhängende Anlage frei wählen.
  matchFelderFuer(getCategory(listing)).forEach(key => {
    if (pd[key] !== undefined && pd[key] !== null && pd[key] !== '') {
      werte[`pub_${key}`] = pd[key];
    }
  });

  // Wer sich anhängt, ist selbst für ein Bündel offen - sonst wäre das
  // Anhängen sinnlos.
  werte.pub_bundle_allowed = 'yes';

  const kategorie = getCategory(listing);

  if (extras.menge) {
    const feld = mengenfeldFuer(kategorie);
    // Die Staffel ist ein Auswahlwert und bleibt Text, die freie Stückzahl
    // ist eine Zahl - das Feld ist als "long" konfiguriert und nimmt keinen
    // String.
    const wert = feld === MENGE_KEY ? parseInt(extras.menge, 10) : extras.menge;
    if (feld !== MENGE_KEY || Number.isFinite(wert)) {
      werte[`pub_${feld}`] = wert;
    }
  }

  return {
    werte,
    quelle: extras.quelle || listing?.attributes?.title || null,
    // Ein lesbarer Satz, was festliegt - "Range Golfbälle · zweistückig,
    // gelb". Er entsteht dort, wo die Feldkonfiguration zur Hand ist, und
    // wird mitgegeben, damit das Formular ihn nicht zweimal bauen muss.
    beschreibung: extras.beschreibung || null,
    kategorie,
    // Wandert mit ins Formular, damit dort steht, warum ein Feld leer blieb.
    hinweis: bundleHinweis(kategorie),
  };
};

const speicher = () => {
  // Serverseitig gibt es kein sessionStorage, und ein privates Fenster kann
  // den Zugriff verweigern. Beides darf die Seite nicht mitreissen.
  try {
    return typeof window !== 'undefined' ? window.sessionStorage : null;
  } catch (e) {
    return null;
  }
};

export const merkeVorlage = vorlage => {
  const s = speicher();
  if (!s || !vorlage) return false;
  try {
    s.setItem(SCHLUESSEL, JSON.stringify(vorlage));
    return true;
  } catch (e) {
    return false;
  }
};

export const holeVorlage = () => {
  const s = speicher();
  if (!s) return null;
  try {
    const roh = s.getItem(SCHLUESSEL);
    return roh ? JSON.parse(roh) : null;
  } catch (e) {
    return null;
  }
};

export const loescheVorlage = () => {
  const s = speicher();
  if (!s) return;
  try {
    s.removeItem(SCHLUESSEL);
  } catch (e) {
    // Nichts zu tun - eine Vorlage, die liegen bleibt, wird beim naechsten
    // Anhaengen ueberschrieben und verschwindet mit der Sitzung.
  }
};
